import React, { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  Paper,
  ListItem,
  ListItemText,
  Typography,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import ControlCameraIcon from "@mui/icons-material/ControlCamera";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

const getScheduleTypeLabel = (schType) => {
  switch (schType) {
    case "Rental":
      return "Rental Income";
    case "FARegister":
      return "FA Register";
    case "FASummary":
      return "FA Summary";
    case "FAAdditions":
      return "FA Additions";
    case "FADisposals":
      return "FA Disposals";
    case "CASummary":
      return "CA Summary";
    case "HirePurchase":
      return "Hire Purchase";
    case "S19ASchedule":
      return "S19A CA Schedule";
    case "S19Schedule":
      return "S19 CA Schedule";
    case "S19BSchedule":
      return "S19B CA Schedule";
    case "S19DSchedule":
      return "S19D CA Schedule";
    case "S16Schedule":
      return "S16 CA Schedule";
    default:
      return schType;
  }
};

const BaseDndItem = ({
  scheduleData,
  index,
  schedule,
  moveSchedule,
  reorderSchedule,
  onOpenSchedule,
  showFullWidth = false,
  scheduleLabel = "Schedule No",
  type = "schedule",
  canMoveSchedule = true,
  hideDragHandle = false,
}) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
    setActivatorNodeRef,
  } = useSortable({
    id: scheduleData.id,
    disabled: !canMoveSchedule,
  });

  // 🎨 拖拽样式 - 更平滑的过渡和阴影
  const dragStyle = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.95 : 1,
    zIndex: isDragging ? 1000 : "auto",
    ...(isDragging && {
      boxShadow: "0 20px 60px rgba(96, 63, 151, 0.3)",
      cursor: "grabbing",
    }),
  };

  // 🎨 放置预览样式 - 更清晰的插入指示线
  const getDropPreviewStyle = () => {
    if (!isOver || isDragging) return {};

    return {
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        right: 0,
        height: "3px",
        backgroundColor: theme.palette.primary.main,
        top: 0,
        borderRadius: "3px",
        boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.5)}`,
        animation: "pulse 1.5s ease-in-out infinite",
      },
      "@keyframes pulse": {
        "0%, 100%": {
          opacity: 1,
          boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.5)}`,
        },
        "50%": {
          opacity: 0.7,
          boxShadow: `0 0 16px ${alpha(theme.palette.primary.main, 0.8)}`,
        },
      },
    };
  };

  // 🎨 拖拽时的占位符样式
  const placeholderStyle = {
    ...(isDragging && {
      backgroundColor: alpha(theme.palette.primary.main, 0.03),
      border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
      borderRadius: theme.shape.borderRadius * 2,
    }),
  };

  return (
    <Paper
      ref={setNodeRef}
      elevation={isDragging ? 6 : isHovered ? 2 : 0}
      sx={{
        display: "flex",
        alignItems: "center",
        mb: 1.5,
        borderRadius: 3,
        backgroundColor: isDragging
          ? theme.palette.background.paper
          : isOver
            ? alpha(theme.palette.primary.main, 0.04)
            : "white",
        padding: 1,
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        border: "1px solid",
        borderColor: isDragging
          ? theme.palette.primary.main
          : isHovered
            ? alpha(theme.palette.primary.main, 0.3)
            : alpha(theme.palette.divider, 0.15),
        boxShadow: isDragging
          ? "0 20px 60px rgba(96, 63, 151, 0.25)"
          : isHovered
            ? "0 4px 20px rgba(0, 0, 0, 0.08)"
            : "0 1px 3px rgba(0, 0, 0, 0.04)",
        position: "relative",
        overflow: "visible",
        ...dragStyle,
        ...getDropPreviewStyle(),
        ...placeholderStyle,
        "&:hover": {
          borderColor: alpha(theme.palette.primary.main, 0.4),
          backgroundColor: alpha(theme.palette.primary.main, 0.02),
          transform: isDragging ? "none" : "translateY(-2px)",
        },
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* 🎯 拖拽手柄 - 增强视觉反馈 */}
      <Box
        ref={canMoveSchedule ? setActivatorNodeRef : null}
        {...(canMoveSchedule ? { ...attributes, ...listeners } : {})}
        sx={{
          display: hideDragHandle ? "none" : "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 1.2,
          backgroundColor: isDragging
            ? alpha(theme.palette.primary.main, 0.12)
            : isHovered && canMoveSchedule
              ? alpha(theme.palette.primary.main, 0.06)
              : "transparent",
          cursor: canMoveSchedule ? "grab" : "default",
          borderRadius: 2,
          mr: 1.5,
          transition: "all 0.2s ease",
          position: "relative",
          ...(canMoveSchedule
            ? {
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  transform: "scale(1.08)",
                },
                "&:active": {
                  cursor: "grabbing",
                  transform: "scale(0.95)",
                  backgroundColor: alpha(theme.palette.primary.main, 0.15),
                },
                // 拖拽时可用的视觉提示
                "&::after": {
                  content: canMoveSchedule ? '""' : "none",
                  position: "absolute",
                  inset: -4,
                  borderRadius: 4,
                  border: `1px dashed ${alpha(theme.palette.primary.main, 0)}`,
                  transition: "border-color 0.2s ease",
                },
                "&:hover::after": {
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                },
              }
            : {
                opacity: 0.4,
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }),
        }}
      >
        {canMoveSchedule ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              alignItems: "center",
            }}
          >
            {/* 双点拖拽图标 - 更直观的拖拽暗示 */}
            <DragIndicatorIcon
              fontSize='small'
              sx={{
                color: isDragging
                  ? "primary.main"
                  : isHovered
                    ? "primary.dark"
                    : "grey.500",
                transition: "color 0.2s ease, transform 0.2s ease",
                transform: isHovered && !isDragging ? "translateY(0)" : "none",
              }}
            />
          </Box>
        ) : (
          <Tooltip
            title="You don't have permission to reorder this item"
            placement='right'
            arrow
          >
            <ControlCameraIcon
              fontSize='small'
              sx={{
                color: "grey.400",
              }}
            />
          </Tooltip>
        )}
      </Box>

      {/* 📋 日程详情 - 优化点击区域和悬停效果 */}
      <ListItem
        button
        onClick={() => onOpenSchedule(schedule, schedule.scheduleType)}
        disabled={isDragging}
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          py: 1.5,
          px: 2,
          borderRadius: 2,
          transition: "all 0.2s ease",
          cursor: isDragging ? "grabbing" : "pointer",
          "&:hover": {
            backgroundColor: isDragging
              ? "transparent"
              : alpha(theme.palette.primary.main, 0.06),
          },
          "&.Mui-disabled": {
            cursor: "grabbing",
            opacity: 0.7,
          },
        }}
      >
        <ListItemText
          primary={
            <Tooltip
              title={scheduleData.scheduleTitle}
              placement='top'
              arrow
              disableHoverListener={showFullWidth}
            >
              <Typography
                variant='subtitle2'
                fontWeight='600'
                color={isDragging ? "text.secondary" : "text.primary"}
                sx={{
                  textTransform: "uppercase",
                  fontSize: "0.875rem",
                  maxWidth: showFullWidth ? "100%" : "120px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  letterSpacing: "0.5px",
                  transition: "color 0.2s ease",
                }}
              >
                {!showFullWidth
                  ? scheduleData.scheduleTitle.length > 18
                    ? `${scheduleData.scheduleTitle.substring(0, 18)}...`
                    : scheduleData.scheduleTitle
                  : scheduleData.scheduleTitle.length > 32
                    ? `${scheduleData.scheduleTitle.substring(0, 32)}...`
                    : scheduleData.scheduleTitle}
              </Typography>
            </Tooltip>
          }
          secondary={
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                mt: 0.5,
                opacity: isDragging ? 0.7 : 1,
                transition: "opacity 0.2s ease",
              }}
            >
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: isDragging ? "primary.main" : "grey.400",
                    transition: "background-color 0.2s ease",
                  }}
                />
                {scheduleLabel}: {scheduleData.scheduleNo}
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{
                  fontSize: showFullWidth ? "0.75rem" : "0.65rem",
                  opacity: 0.85,
                  mt: 0.2,
                }}
              >
                Type: {getScheduleTypeLabel(schedule.scheduleType || "N/A")}
              </Typography>
            </Box>
          }
        />
        {/* 右侧箭头 - 拖拽时隐藏 */}
        <IconButton
          edge='end'
          disabled={isDragging}
          sx={{
            ml: "auto",
            color: isDragging ? "grey.400" : "grey.600",
            transition: "all 0.2s ease",
            opacity: isDragging ? 0.5 : 1,
            "&:hover": {
              color: isDragging ? "grey.400" : "primary.main",
              backgroundColor: isDragging
                ? "transparent"
                : alpha(theme.palette.primary.main, 0.08),
              transform: isDragging ? "none" : "translateX(4px)",
            },
          }}
        >
          <ChevronRightIcon fontSize='small' />
        </IconButton>
      </ListItem>

      {isDragging && (
        <Box
          sx={{
            position: "absolute",
            top: -8,
            right: -8,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            px: 1,
            py: 0.5,
            borderRadius: 2,
            fontSize: "0.7rem",
            fontWeight: 600,
            boxShadow: "0 2px 8px rgba(96, 63, 151, 0.3)",
            zIndex: 1001,
            animation: "fadeIn 0.2s ease",
            "@keyframes fadeIn": {
              "0%": { opacity: 0, transform: "scale(0.8)" },
              "100%": { opacity: 1, transform: "scale(1)" },
            },
          }}
        >
          Moving...
        </Box>
      )}
    </Paper>
  );
};

export default BaseDndItem;
